// Use namespace imports to avoid hard-coupling to specific named exports at build time.
import * as MmtEventLogger from '@mmt/event-logger';
import * as GtEventLoggerWeb from '@gt-common/event-logger-web';
import { logSeoPageLoadFromInitialState } from './seoPageLoad';

declare global {
  interface Window {
    AnalyticsSdk?: unknown;
    __XDM_ADOBE_SCRIPT_LOADED__?: boolean;
  }
}

let _isInitialized = false;
let _initPromise: Promise<boolean> | null = null;
let _initResolve: ((ready: boolean) => void) | null = null;

// Experiment gating (Route Operator allowlist)
const SESSION_FLAG_KEY = 'enable_xdm_logging|' as const;

const scheduleWhenIdle = (cb: () => void): void => {
  const w = window as unknown as {
    requestIdleCallback?: (fn: () => void) => void;
    setTimeout: (fn: () => void, ms: number) => number;
  };
  const schedule = w.requestIdleCallback
    ? w.requestIdleCallback
    : (fn: () => void) => w.setTimeout(fn, 0);
  schedule(cb);
};

/**
 * Single entrypoint for main.js:
 * - Enables experimentation only for allowlisted Route Operator ids
 * - Only initializes Adobe/XDM (script injection) and sets the session flag
 * - Does not trigger any XDM logging
 */
export const initXdmExperiment = (): void => {
  try {
    if (typeof window === 'undefined') return;

    // removing xdm whitelisting

    sessionStorage.setItem(SESSION_FLAG_KEY, 'true');
    scheduleWhenIdle(() => {
      // Initialize Adobe/XDM (script injection)
      initAdobeDataLayer();

      // Log page-load event (same entrypoint as earlier).
      // The logger is session-gated (enable_xdm_logging|), and will no-op if init isn't ready.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      console.log(
        'logSeoPageLoadFromInitialState',
        (window as any).__INITIAL_STATE__,
      );
      void logSeoPageLoadFromInitialState((window as any).__INITIAL_STATE__);
    });
  } catch {
    // no-op
  }
};

const extractScriptSrc = (scriptHtml: string): string | null => {
  try {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = scriptHtml;
    const scriptEl = tempDiv.querySelector('script');
    const src = scriptEl?.getAttribute('src') || scriptEl?.src;
    return src || null;
  } catch (e) {
    return null;
  }
};

export const getAnalyticsSdk = () => {
  try {
    if (typeof window === 'undefined') return null;
    if (window.AnalyticsSdk) return window.AnalyticsSdk;

    const XDMWrapper = (GtEventLoggerWeb as Record<string, unknown>)
      ?.XDMWrapper as
      | undefined
      | {
          initAndSetAnalyticsSdk?: (args: Record<string, unknown>) => unknown;
        };
    if (!XDMWrapper?.initAndSetAnalyticsSdk) return null;

    const sdk = XDMWrapper.initAndSetAnalyticsSdk({ org: 'MMT' });
    window.AnalyticsSdk = sdk;
    return sdk;
  } catch (e) {
    return null;
  }
};

/**
 * Awaitable init that resolves when the Adobe data-layer script has loaded (or times out).
 * This prevents page-load logging from racing the async script injection.
 */
export const initAdobeDataLayerAsync = (timeoutMs = 5000): Promise<boolean> => {
  try {
    if (typeof window === 'undefined') return Promise.resolve(false);

    if (window.__XDM_ADOBE_SCRIPT_LOADED__) return Promise.resolve(true);
    if (_initPromise) return _initPromise;

    _initPromise = new Promise<boolean>((resolve) => {
      _initResolve = resolve;

      // Timeout so we never block forever
      window.setTimeout(() => {
        if (window.__XDM_ADOBE_SCRIPT_LOADED__) {
          resolve(true);
        } else {
          resolve(false);
        }
      }, timeoutMs);
    });

    // Kick off init side-effect
    initAdobeDataLayer();
    return _initPromise;
  } catch {
    return Promise.resolve(false);
  }
};

/**
 * Bootstrap Adobe Data Layer script injection (async) and prepare the SDK singleton.
 * Per requirement: must call initAdobeDataLayerMMT(isDev ? 'development' : 'production')
 */
export const initAdobeDataLayer = (): void => {
  try {
    if (typeof window === 'undefined') return;
    if (_isInitialized) return;
    _isInitialized = true;

    const isDev = process.env.NODE_ENV === 'development';
    const initAdobeDataLayerMMT = (MmtEventLogger as Record<string, unknown>)
      ?.initAdobeDataLayerMMT as undefined | ((env: string) => string);

    if (typeof initAdobeDataLayerMMT !== 'function') {
      return;
    }

    // Minimal bootstrap requirement
    const scriptHtml = initAdobeDataLayerMMT(
      isDev ? 'development' : 'production',
    );

    const scriptSrc = extractScriptSrc(String(scriptHtml || ''));
    if (!scriptSrc) {
      return;
    }

    // Avoid duplicate injection
    const existing = document.querySelector(`script[src="${scriptSrc}"]`);
    if (existing) {
      window.__XDM_ADOBE_SCRIPT_LOADED__ = true;
      // Still attempt SDK init; wrapper is expected to be idempotent.
      getAnalyticsSdk();
      _initResolve?.(true);
      return;
    }

    const script = document.createElement('script');
    script.src = scriptSrc;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.__XDM_ADOBE_SCRIPT_LOADED__ = true;
      getAnalyticsSdk();
      _initResolve?.(true);
    };
    script.onerror = () => {
      _initResolve?.(false);
    };
    document.head.appendChild(script);
  } catch (e) {
    // Never block render
  }
};
